import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useDispatch, useSelector } from "react-redux";
import { toggleFilterCollapsed } from "../../store/slices/sidebarFilterSlice";
import api from "../../api/axios";
import { toast } from "react-toastify";
import qs from 'qs';
import { useNavigate } from 'react-router-dom';

import type { SortOption } from "../user/sell/Sell";
import type { OfferDTO } from "../user/on-sale/types";
import MarketplaceOfferCard from "./MarketplaceOfferCard";
import OfferDetailModal from '../skin-db/offer-details/OfferDetailModal';
import BuyConfirmModal from './modals/BuyConfirmModal';
import { RootState } from '../../store';
import { addToCartLocally, removeItemFromCartLocally } from '../../store/slices/cartSlice';
import { SteamAccount, subtractFromWalletAfterBuy } from '../../store/slices/authSlice';
import {InventoryFilterState} from '../skin-db/database/filter/SkinDbFilter';

type Props = {
  filters: InventoryFilterState;
  sortBy: SortOption;
  searchTerm: string;
  onSearchChange: (val: string) => void;
  onSortChange: (val: SortOption) => void;
  onResetFilters: () => void;
  onUpdateFilters: (f: InventoryFilterState) => void;
  highlightOfferId?: string;
};

const PAGE_SIZE = 24;

const DEFAULTS = {
  minPrice: 0,
  maxPrice: 100000,
  floatMin: 0,
  floatMax: 1,
  minFade: 80,
  maxFade: 100,
  minBlue: 0,
  maxBlue: 100,
  paintSeed: null as number | null,
};

const MarketplaceGrid: React.FC<Props> = ({
                                            filters,
                                            sortBy,
                                            searchTerm,
                                            onSearchChange,
                                            onSortChange,
                                            onResetFilters,
                                            onUpdateFilters,
                                            highlightOfferId
                                          }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth);
  const isAuthenticated = auth.isAuthenticated; // Dynamic check for guest state

  const [offers, setOffers] = useState<OfferDTO[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState(false);

  const [selectedOffer, setSelectedOffer] = useState<OfferDTO | null>(null);
  const [isFetchingSingle, setIsFetchingSingle] = useState(false);

  const [cartLoading, setCartLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buyNowSelectedSteamAccount, setBuyNowSelectedSteamAccount] = useState<SteamAccount | null>(null);

  const pageRef = useRef(0);
  const isFetching = useRef(false);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const chipContainerRef = useRef<HTMLDivElement | null>(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const fetchSingleOffer = async (id: string) => {
    if (isFetchingSingle) return;
    setIsFetchingSingle(true);
    try {
      const endpoint = isAuthenticated ? `/marketplace/offers/${id}` : `/guest/marketplace/offers/${id}`;
      const res = await api.get(endpoint);
      if (res.data) {
        setSelectedOffer(res.data);
      }
    } catch (e: any) {
      console.error("Failed to fetch specific offer", e);
      toast.error("Offer not found or no longer active.");
      navigate('/market');
    } finally {
      setIsFetchingSingle(false);
    }
  };

  useEffect(() => {
    if (highlightOfferId) {
      const found = offers.find(o => String(o.id) === highlightOfferId);
      if (found) {
        setSelectedOffer(found);
      } else if (!isFetchingSingle) {
        fetchSingleOffer(highlightOfferId);
      }
    } else {
      setSelectedOffer(null);
    }
  }, [highlightOfferId, offers, isAuthenticated]);

  const buildParams = (currentPage: number) => {
    const params: Record<string, any> = {
      page: currentPage,
      size: PAGE_SIZE,
      sort: sortBy === "price_asc" ? "priceMinor,asc" :
          sortBy === "price_desc" ? "priceMinor,desc" :
              sortBy === "title_asc" ? "item.marketHashName,asc" :
                  sortBy === "title_desc" ? "item.marketHashName,desc" :
                      "priceMinor,desc",
    };

    if (searchTerm.trim().length > 0) params.name = searchTerm;

    const arrayKeys: (keyof InventoryFilterState)[] = [
      'exterior', 'rarity', 'extra', 'type', 'phase', 'rarePattern', 'unique',
      'pistols', 'smg', 'rifles', 'snipers', 'shotguns', 'machineguns', 'equipment', 'knives'
    ];

    arrayKeys.forEach((key) => {
      const value = filters[key];
      if (Array.isArray(value) && value.length > 0) params[key] = value;
    });

    (Object.entries(DEFAULTS) as [keyof typeof DEFAULTS, any][]).forEach(([key, def]) => {
      const current = filters[key as keyof InventoryFilterState];
      if (key !== 'paintSeed' && current !== def) {
        params[key] = current;
      } else if (key === 'paintSeed' && current !== null && String(current) !== "") {
        params[key] = current;
      }
    });
    return params;
  };

  const loadNext = useCallback(async (isReset = false) => {
    if (isFetching.current || (!hasMore && !isReset) || error) return;
    isFetching.current = true;
    setLoading(true);
    setError(false);
    const currentPage = isReset ? 0 : pageRef.current;
    try {
      const params = buildParams(currentPage);
      const queryString = qs.stringify(params, { arrayFormat: 'brackets', encode: true, skipNulls: true });
      const endpoint = isAuthenticated ? `/marketplace/offers?${queryString}` : `/guest/marketplace/offers?${queryString}`;
      const res = await api.get(endpoint);
      const content: OfferDTO[] = res.data.content ?? [];

      setOffers((prev) => isReset ? content : [...prev, ...content.filter(o => !prev.some(p => p.id === o.id))]);
      setHasMore(!res.data.last);
      pageRef.current = currentPage + 1;
    } catch (e: any) {
      setError(true);
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetching.current = false;
      setInitialLoadDone(true);
    }
  }, [hasMore, error, filters, sortBy, searchTerm, isAuthenticated]);

  useEffect(() => {
    pageRef.current = 0;
    setHasMore(true);
    setInitialLoadDone(false);
    setError(false);
    loadNext(true);
  }, [JSON.stringify(filters), sortBy, searchTerm, isAuthenticated]);

  useEffect(() => {
    const currentLoader = loaderRef.current;
    if (!currentLoader || !hasMore || error || !initialLoadDone) return;
    const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isFetching.current && hasMore) loadNext();
        },
        { root: scrollContainerRef.current, rootMargin: "200px" }
    );
    observer.observe(currentLoader);
    return () => { if (currentLoader) observer.unobserve(currentLoader); observer.disconnect(); };
  }, [loadNext, hasMore, error, initialLoadDone]);

  const handleAddToCart = async (offer: OfferDTO) => {
    if (!auth.selectedSteamAccount?.id) {
      toast.error('No Steam Account selected');
      return;
    }
    setCartLoading(true);
    try {
      await api.post('/cart/add', {
        offerId: offer.id,
        priceMinor: offer.priceMinor,
        item: offer.item,
        buyerSelectedSteamAccountId: auth.selectedSteamAccount.id
      });

      // Ensure IDs are numbers to match CartItemDTO
      dispatch(addToCartLocally({
        offerId: offer.id,
        priceMinor: offer.priceMinor,
        item: offer.item as any, // Cast if ItemDTO structure slightly differs but catalog matches
        selectedBuyerSteamAccountId: auth.selectedSteamAccount.id
      }));
      toast.success('Added to cart');
    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Failed to add');
    } finally {
      setCartLoading(false);
    }
  };

  const handleRemoveFromCart = async (offerId: string) => {
    setCartLoading(true);
    try {
      await api.delete(`/cart/${offerId}`);
      dispatch(removeItemFromCartLocally(offerId));
      toast.info('Removed from cart');
    } catch (error: any) {
      toast.error('Failed to remove');
    } finally {
      setCartLoading(false);
    }
  };

  const handleBuyNowTrigger = (offer: OfferDTO) => {
    setBuyNowSelectedSteamAccount(auth.selectedSteamAccount);
    setConfirmOpen(true);
  };

  const handleBuyConfirm = async () => {
    if (!selectedOffer || !buyNowSelectedSteamAccount) return;
    setBuyLoading(true);
    try {
      await api.post('/buy', {
        offerId: selectedOffer.id,
        priceMinor: selectedOffer.priceMinor,
        buyerSelectedSteamAccountId: buyNowSelectedSteamAccount.id
      });
      dispatch(subtractFromWalletAfterBuy(selectedOffer.priceMinor));
      dispatch(removeItemFromCartLocally(selectedOffer.id));
      setConfirmOpen(false);
      handleBuySuccess(selectedOffer.id);
      toast.success("Purchase successful!");
    } catch (error: any) {
      toast.error(error?.response?.data ?? 'Buy failed');
    } finally {
      setBuyLoading(false);
    }
  };

  const handleBuySuccess = (offerId: string | number) => {
    const updatedStatus = 'BOUGHT';

    setOffers(prev => prev.map(o =>
        String(o.id) === String(offerId)
            ? { ...o, status: updatedStatus }
            : o
    ));

    setSelectedOffer(prev => {
      if (prev && String(prev.id) === String(offerId)) {
        return { ...prev, status: updatedStatus };
      }
      return prev;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!chipContainerRef.current) return;
    isDown.current = true;
    startX.current = e.pageX - chipContainerRef.current.offsetLeft;
    scrollLeft.current = chipContainerRef.current.scrollLeft;
  };
  const handleMouseLeaveOrUp = () => { isDown.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current || !chipContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - chipContainerRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;
    chipContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const removeFilter = (key: keyof InventoryFilterState, valueToRemove?: any) => {
    const currentVal = filters[key];
    if (Array.isArray(currentVal)) {
      onUpdateFilters({ ...filters, [key]: currentVal.filter(v => v !== valueToRemove) });
    } else {
      const defaultValue = (DEFAULTS as any)[key] !== undefined ? (DEFAULTS as any)[key] : (typeof currentVal === 'number' ? 0 : "");
      onUpdateFilters({ ...filters, [key]: defaultValue });
    }
  };

  const activeChips = (() => {
    const chips: { key: keyof InventoryFilterState; label: string; value?: any }[] = [];
    if (filters.minPrice !== DEFAULTS.minPrice) chips.push({ key: 'minPrice', label: `$${filters.minPrice}+` });
    if (filters.maxPrice !== DEFAULTS.maxPrice) chips.push({ key: 'maxPrice', label: `<$${filters.maxPrice}` });
    if (filters.floatMin !== DEFAULTS.floatMin) chips.push({ key: 'floatMin', label: `F-Min: ${filters.floatMin}` });
    if (filters.floatMax !== DEFAULTS.floatMax) chips.push({ key: 'floatMax', label: `F-Max: ${filters.floatMax}` });
    if (filters.paintSeed !== null && String(filters.paintSeed) !== "") chips.push({ key: 'paintSeed', label: `Seed: ${filters.paintSeed}` });
    if (filters.minFade !== DEFAULTS.minFade) chips.push({ key: 'minFade', label: `Fade: ${filters.minFade}%+` });
    if (filters.maxFade !== DEFAULTS.maxFade) chips.push({ key: 'maxFade', label: `<${filters.maxFade}%` });
    if (filters.minBlue !== DEFAULTS.minBlue) chips.push({ key: 'minBlue', label: `Blue: ${filters.minBlue}%+` });
    if (filters.maxBlue !== DEFAULTS.maxBlue) chips.push({ key: 'maxBlue', label: `<${filters.maxBlue}%` });
    const arrayFilterKeys: (keyof InventoryFilterState)[] = ['exterior', 'rarity', 'extra', 'type', 'phase', 'rarePattern', 'unique', 'pistols', 'smg', 'rifles', 'snipers', 'shotguns', 'machineguns', 'equipment', 'knives'];
    arrayFilterKeys.forEach(key => {
      const val = filters[key];
      if (Array.isArray(val)) val.forEach(item => chips.push({ key, label: item, value: item }));
    });
    return chips;
  })();

  const renderChips = (isMobile: boolean) => {
    if (activeChips.length === 0) return null;
    return (
        <div
            className={`${isMobile ? 'lg:hidden' : 'hidden lg:flex'} flex items-center gap-2 overflow-x-auto px-1 scrollbar-none select-none cursor-grab active:cursor-grabbing`}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeaveOrUp}
            onMouseUp={handleMouseLeaveOrUp}
            onMouseMove={handleMouseMove}
            ref={isMobile ? null : chipContainerRef}
        >
          {activeChips.length > 1 && (
              <button onClick={onResetFilters} className="flex items-center justify-center bg-red-500/20 text-red-500 p-2 rounded-lg shrink-0 border border-red-500/30 active:scale-95 transition-transform">
                <DeleteSweepIcon fontSize="small" />
              </button>
          )}
          {activeChips.map((chip, idx) => (
              <div key={`${chip.key}-${idx}`} className="flex items-center bg-neutral-800 border border-neutral-700 rounded-lg overflow-hidden shrink-0">
                <button onClick={() => removeFilter(chip.key, chip.value)} className="px-2 py-1.5 bg-neutral-700/50 hover:bg-red-600 group transition-colors border-r border-neutral-700">
                  <CloseIcon sx={{ fontSize: 14 }} className="text-neutral-400 group-hover:text-white" />
                </button>
                <span className="px-3 text-[11px] text-neutral-300 font-bold whitespace-nowrap uppercase">{chip.label}</span>
              </div>
          ))}
        </div>
    );
  };

  return (
      <Box className="flex flex-col h-[88vh]">
        <div className="flex flex-col gap-2 mb-2.5">
          {renderChips(true)}
          <div className="flex justify-between items-center py-3 px-4 bg-neutral-900/80 backdrop-blur-md shadow-lg shadow-dark-primary/30 md:rounded-xl rounded-lg border-none relative min-h-[60px]">
            <div className="flex gap-3 items-center flex-shrink-0">
              <IconButton onClick={() => dispatch(toggleFilterCollapsed())} className="bg-neutral-800 hover:bg-neutral-700 transition-colors" sx={{ borderRadius: '8px', border: 'none', padding: '8px' }}>
                <FilterListIcon className="text-dark-primary text-xl" />
              </IconButton>
              <TextField className="md:w-64 w-36" size="small" placeholder="Search items..." value={searchTerm} onChange={(e) => onSearchChange(e.target.value)}
                         sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#262626', '& fieldset': { border: 'none' }, '& input': { color: '#e5e5e5', fontSize: '14px' } } }}
              />
            </div>
            <div className="hidden lg:flex flex-1 mx-4 overflow-hidden items-center">{renderChips(false)}</div>
            <FormControl size="small" className="md:w-52 w-40 flex-shrink-0">
              <Select value={sortBy} onChange={(e) => onSortChange(e.target.value as SortOption)} variant="outlined" MenuProps={{ disableScrollLock: true }}
                      sx={{ borderRadius: '8px', backgroundColor: '#262626', color: '#e5e5e5', fontSize: '14px', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiSelect-icon': { color: '#737373' } }}
              >
                <MenuItem value="price_desc">Price: Highest</MenuItem>
                <MenuItem value="price_asc">Price: Lowest</MenuItem>
                <MenuItem value="title_asc">Title A → Z</MenuItem>
                <MenuItem value="title_desc">Title Z → A</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pr-1">
          {initialLoadDone && offers.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <SearchOffIcon sx={{ fontSize: 80, color: '#4b5563', mb: 2 }} />
                <Typography variant="h5" className="text-white font-bold mb-2">{error ? "Failed to load" : "No items found"}</Typography>
                <Button variant="contained" className="bg-dark-primary hover:bg-dark-primary/80 normal-case font-bold px-8" onClick={() => error ? loadNext(true) : onResetFilters()}>
                  {error ? "Try Again" : "View all market offers"}
                </Button>
              </div>
          ) : (
              <div className="grid gap-4 pb-4 w-full grid-cols-2 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
                {offers.map((offer) => (
                    <MarketplaceOfferCard key={offer.id} offer={offer} onBuySuccess={handleBuySuccess} />
                ))}
                {loading && [...Array(12)].map((_, i) => (
                    <Card key={i} className="bg-[#1c1f26] border border-gray-700">
                      <CardContent className="flex flex-col !p-3">
                        <Skeleton width="80%" height={30}/>
                        <Skeleton width="60%" height={20} className="mt-1"/>
                        <Skeleton variant="rectangular" height={160} className="my-3 rounded-md"/>
                        <Skeleton width="40%" height={25} className="mb-2"/>
                        <div className="h-[40px] w-full">
                          <Skeleton width="70%" height={10} className="rounded-full"/>
                        </div>
                        <Skeleton variant="rectangular" height={36} className="mt-auto rounded-md"/>
                      </CardContent>
                    </Card>
                ))}
              </div>
          )}
          {!error && hasMore && <div ref={loaderRef} className="h-10 w-full flex items-center justify-center" />}
        </div>

        {selectedOffer && (
            <OfferDetailModal
                open={!!selectedOffer}
                onClose={() => {
                  setSelectedOffer(null);
                  if (highlightOfferId) navigate('/market');
                }}
                offer={selectedOffer}
                onAddToCart={handleAddToCart}
                onRemoveFromCart={handleRemoveFromCart}
                onBuyNow={handleBuyNowTrigger}
                cartLoading={cartLoading}
                buyLoading={buyLoading}
            />
        )}

        {confirmOpen && selectedOffer && (
            <BuyConfirmModal
                open={confirmOpen}
                loading={buyLoading}
                offer={selectedOffer}
                selectedSteamAccount={buyNowSelectedSteamAccount}
                setSelectedSteamAccount={setBuyNowSelectedSteamAccount}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleBuyConfirm}
            />
        )}
      </Box>
  );
};

export default MarketplaceGrid;